import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Mail, MailOpen, Trash2, Clock } from 'lucide-react';
import { AdminEmptyState, AdminLoadingState, AdminPageHeader, AdminSurface } from '@/components/admin/AdminPrimitives';

export default function AdminMessages() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMsg, setSelectedMsg] = useState(null);

  const fetchMessages = async () => {
    try {
      const res = await api.get('/admin/messages');
      setMessages(res.data);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetchMessages(); }, []);

  const markRead = async (id) => {
    try {
      await api.put(`/admin/messages/${id}/read`);
      fetchMessages();
    } catch {}
  };

  const deleteMessage = async (id) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await api.delete(`/admin/messages/${id}`);
      toast.success('Message deleted');
      if (selectedMsg?.id === id) setSelectedMsg(null);
      fetchMessages();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const openMessage = (msg) => {
    setSelectedMsg(msg);
    if (!msg.read) markRead(msg.id);
  };

  if (loading) {
    return <AdminLoadingState label="Loading messages" />;
  }

  return (
    <div data-testid="admin-messages-page">
      <AdminPageHeader
        eyebrow="Inbox"
        title="Messages"
        description={`Contact form submissions (${messages.filter(m => !m.read).length} unread)`}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Messages List */}
        <div className="lg:col-span-1 space-y-2">
          {messages.length === 0 ? (
            <AdminEmptyState icon={Mail} title="No messages yet" description="New contact form submissions will appear here." />
          ) : (
            messages.map((msg) => (
              <button
                key={msg.id}
                data-testid={`message-item-${msg.id}`}
                onClick={() => openMessage(msg)}
                className={`mscm-admin-list-item w-full text-left p-4 rounded-[8px] border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1E4FA1]/30 ${
                  selectedMsg?.id === msg.id
                    ? 'bg-[#EBF2FA] border-[#1E4FA1]/30'
                    : msg.read
                      ? 'bg-white border-[#E2E8F0] hover:bg-[#F8FAFC]'
                      : 'bg-white border-[#1E4FA1]/20 hover:bg-[#F8FAFC]'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  {msg.read ? (
                    <MailOpen size={14} className="text-[#94A3B8]" />
                  ) : (
                    <Mail size={14} className="text-[#1E4FA1]" />
                  )}
                  <span className={`text-sm font-medium truncate ${msg.read ? 'text-[#475569]' : 'text-[#0F172A]'}`}>
                    {msg.name}
                  </span>
                </div>
                <p className="text-xs text-[#94A3B8] truncate">{msg.email}</p>
                <p className="text-xs text-[#94A3B8] mt-1 flex items-center gap-1">
                  <Clock size={10} /> {formatDate(msg.created_at)}
                </p>
              </button>
            ))
          )}
        </div>

        {/* Message Detail */}
        <div className="lg:col-span-2">
          {selectedMsg ? (
            <AdminSurface className="p-6" data-testid="message-detail">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-bold text-[#0F172A]">{selectedMsg.name}</h3>
                  <a href={`mailto:${selectedMsg.email}`} className="text-sm text-[#1E4FA1] hover:underline">
                    {selectedMsg.email}
                  </a>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="ghost" size="sm"
                    onClick={() => deleteMessage(selectedMsg.id)}
                    className="text-red-500 hover:bg-red-50"
                    aria-label="Delete message"
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
              <p className="text-xs text-[#94A3B8] mb-4">{formatDate(selectedMsg.created_at)}</p>
              <div className="rounded-[8px] border border-[#E2E8F0]/70 bg-[#F8FBFF] p-4">
                <p className="text-[#475569] leading-relaxed whitespace-pre-wrap">{selectedMsg.message}</p>
              </div>
              <div className="mt-4">
                <a
                  href={`mailto:${selectedMsg.email}?subject=Re: Your message to Million Stars Community Ministry`}
                  data-testid="reply-email-link"
                  className="inline-flex min-h-10 items-center gap-2 rounded-full bg-[#1E4FA1] px-5 py-2 text-sm font-semibold text-white shadow-[0_12px_28px_rgba(30,79,161,0.22)] transition-all hover:-translate-y-0.5 hover:bg-[#153A7A] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1E4FA1]/30"
                >
                  <Mail size={14} /> Reply via Email
                </a>
              </div>
            </AdminSurface>
          ) : (
            <AdminEmptyState icon={Mail} title="Select a message" description="Choose a conversation from the list to view details and reply." />
          )}
        </div>
      </div>
    </div>
  );
}
